/**
 * One setting's value pill and the chips it opens (T-B1): `12 h ▾` → a grid of the rungs the
 * setting may take, the current one ringed, and under them what the current value means today
 * (the notice's nearest start, the horizon's last day).
 *
 * Base UI's popover supplies the behaviour: Escape and a click outside close the chips and focus
 * returns to the pill. The chips are a radio group — one Tab stop, the arrows walk it (and wrap),
 * Home and End reach its ends, Enter or Space chooses — and the popover opens with focus ON the
 * current rung, so the keyboard starts where the trainer is. Choosing sends the change and closes;
 * choosing the rung already chosen only closes. The focus ring is the outline, the chosen rung is
 * its ink edge: the two never look alike.
 *
 * A row whose setting has no effect right now (the free-cancellation window while clients may not
 * cancel) keeps its pill focusable, names why through its row's own sentence, and does not open.
 */

import { Popover } from '@base-ui/react/popover';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronGlyph } from '~/shared/ui/VideoGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { PickValue, PolicyPick, SettingsRow } from '../model/policySettings';
import styles from './SettingsTab.module.css';

interface PolicyPickPopoverProps {
  readonly row: Extract<SettingsRow, { kind: 'pick' }>;
  readonly pick: PolicyPick | null;
  readonly onChoose: (value: PickValue) => void;
  /** The row's refusal line, when a change of it was just refused. */
  readonly describedBy?: string;
}

const NEXT: Readonly<Record<string, number>> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

export function PolicyPickPopover({ row, pick, onChoose, describedBy }: PolicyPickPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const reasonId = useId();
  const chips = useRef<(HTMLButtonElement | null)[]>([]);
  const chosen = useRef<HTMLButtonElement | null>(null);
  const label = t('web:booking.settings.choose', { title: row.title, value: row.value });
  const options = pick?.options ?? [];

  if (!row.enabled) {
    return (
      <>
        <button
          type="button"
          className={`${String(styles.value)} ${String(styles.valueQuiet)}`}
          aria-label={label}
          aria-disabled="true"
          aria-describedby={[reasonId, describedBy].filter(Boolean).join(' ')}
        >
          {row.value}
          <ChevronGlyph className={styles.valueGlyph} direction="down" />
        </button>
        <span id={reasonId} hidden>
          {row.effect}
        </span>
      </>
    );
  }

  const move = (from: number, to: number) => {
    const count = options.length;
    const next = ((to % count) + count) % count;
    if (next !== from) chips.current[next]?.focus();
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className={styles.value} aria-label={label} aria-describedby={describedBy}>
        {row.value}
        <ChevronGlyph className={styles.valueGlyph} direction="down" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner className={styles.positioner} side="bottom" align="end" sideOffset={6}>
          <Popover.Popup className={styles.popup} initialFocus={chosen}>
            <Popover.Title className={hidden.srOnly}>{pick?.title ?? row.title}</Popover.Title>
            <div className={styles.chips} role="radiogroup" aria-label={pick?.title ?? row.title}>
              {options.map((option, index) => (
                <button
                  key={String(option.value)}
                  ref={(node) => {
                    chips.current[index] = node;
                    if (option.selected) chosen.current = node;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={option.selected}
                  tabIndex={option.selected ? 0 : -1}
                  className={`${String(styles.chip)} ${option.selected ? String(styles.chipOn) : ''}`}
                  // The arrows move focus only and do not check, unlike a native radio group, on
                  // purpose: checking a rung sends a PATCH, so it waits for Enter, Space or a click.
                  onKeyDown={(event) => {
                    const step = NEXT[event.key];
                    if (step !== undefined) {
                      event.preventDefault();
                      move(index, index + step);
                    } else if (event.key === 'Home') {
                      event.preventDefault();
                      move(index, 0);
                    } else if (event.key === 'End') {
                      event.preventDefault();
                      move(index, options.length - 1);
                    }
                  }}
                  onClick={() => {
                    setOpen(false);
                    if (!option.selected) onChoose(option.value);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {pick?.footer == null ? null : <p className={styles.popupNote}>{pick.footer}</p>}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
